/* 分析工具 — 早晨重點（盤前多空新聞）
 *
 *  呈現華南投顧「盤前多空新聞」每日文章：利多／利空分區卡片重排版。
 *  資料來源：portal 後端 `/api/morning-focus/*`（後端另行實作）：
 *    GET /morning-focus/list          → [{ id, date, title }] 新→舊
 *    GET /morning-focus/article/{id}  → { id, title, date,
 *                                          sections:[{ name, groups:[{ name, items:[{ seq, title, url }] }] }],
 *                                          disclaimer, source_url }
 *  邊界：sections 可能只有利多（無利空）；group name 可能為空字串（無子分區
 *  時不顯示小節標題）。後端未上線期間端點會 404/502 —— 一律優雅降級為
 *  EmptyState 錯誤卡 + 重試鈕，不白屏。
 *
 *  三態骨架比照 hr-quantdata.jsx；權限：員工 + analyst 家族 + non_staff
 *  （路由白名單見 hr-shell.jsx `_ANALYST_ROUTES` / `_NON_STAFF_ROUTES`）。
 */

/* 分區色調 — 台股慣例紅漲綠跌：利多紅系、利空綠系（色票取 HR.down / HR.accent） */
function MF_tone(name) {
  const n = name || '';
  // 語意色（台股紅漲綠跌）不換；Expressive 只把淡底換成 tonal 底（GxT.tint）
  if (n.indexOf('利多') !== -1) return { fg: HR.down, bg: uiPick('#FAEFEF', GxT.tint.down.bg) };     // 紅系
  if (n.indexOf('利空') !== -1) return { fg: HR.accent, bg: uiPick('#E8F1EE', GxT.tint.accent.bg) };  // 綠系
  return { fg: HR.inkSoft, bg: uiPick(HR.surfaceAlt, GxT.color.s2) };
}

/* 日期 chip 標籤：YYYY-MM-DD → MM/DD（週）；解析失敗退回原字串 */
function MF_dateLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  const wd = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}（${wd}）`;
}

/* 單條新聞：淡色 seq 編號 + 標題外連（hover 比照站內列表列 — 背景轉 surfaceAlt） */
function MF_NewsItem({ item, last }) {
  return (
    <a
      href={item.url} target="_blank" rel="noopener noreferrer"
      title={item.title}
      style={uiStyle({
        display: 'flex', alignItems: 'baseline', gap: 10,
        padding: '8px 6px', borderRadius: 4,
        borderBottom: last ? 'none' : `1px solid ${HR.ruleSoft}`,
        textDecoration: 'none', color: HR.ink, fontSize: FontSizes.xs, lineHeight: 1.55,
        transition: 'background 120ms',
      }, { padding: '10px 8px', borderRadius: GxT.radius.btnSm, fontSize: 14, borderBottom: last ? 'none' : `1px solid ${GxT.color.s3}` })}
      onMouseEnter={(e) => { e.currentTarget.style.background = HR.surfaceAlt; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint,
        width: 22, textAlign: 'right', flexShrink: 0,
      }}>{item.seq != null ? item.seq : ''}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
      <Icon name="ext" size={11} stroke={2} />
    </a>
  );
}

/* 一個分區（利多／利空）一張卡：色帶標頭 + 依 group 分小節 */
function MF_SectionCard({ section }) {
  const tone = MF_tone(section.name);
  const groups = section.groups || [];
  const total = groups.reduce((n, g) => n + ((g.items || []).length), 0);
  return (
    <Card padding="0">
      <div style={uiStyle({
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '13px 24px', background: tone.bg, borderBottom: `1px solid ${HR.rule}`,
      }, { padding: '16px 20px', borderBottom: 'none' })}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: tone.fg, flexShrink: 0 }} />
        <span style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 600, color: tone.fg },
          { fontSize: 17, fontWeight: GxT.weight.title })}>
          {section.name}
        </span>
        <span style={uiStyle({ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: tone.fg, opacity: 0.75 },
          { fontFamily: GxT.font, fontSize: 13, fontWeight: 700, opacity: 1 })}>
          共 {total} 則
        </span>
      </div>
      <div style={uiStyle({ padding: '10px 20px 16px' }, { padding: '8px 12px 14px' })}>
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginTop: gi === 0 ? 0 : 14 }}>
            {/* group name 空字串（無子分區）→ 不顯示小節標題 */}
            {g.name && (
              <div style={uiStyle({
                fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: HR.inkFaint, padding: '8px 6px 4px',
              }, { fontSize: 13, fontWeight: 800, letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, padding: '10px 8px 4px' })}>{g.name}</div>
            )}
            {(g.items || []).map((it, ii) => (
              <MF_NewsItem key={ii} item={it} last={ii === (g.items || []).length - 1} />
            ))}
          </div>
        ))}
        {total === 0 && (
          <div style={uiStyle({ padding: '18px 6px', color: HR.inkFaint, fontSize: FontSizes.xs }, { color: GxT.color.onV, fontSize: 14 })}>此分區今日無新聞</div>
        )}
      </div>
    </Card>
  );
}

function MorningFocusPage() {
  // 文章清單（近 5 篇）：'loading' | 'ready' | 'error'
  const [listPhase, setListPhase] = React.useState('loading');
  const [listErr, setListErr] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null);
  // 單篇文章：'loading' | 'ready' | 'error'
  const [artPhase, setArtPhase] = React.useState('loading');
  const [artErr, setArtErr] = React.useState('');
  const [article, setArticle] = React.useState(null);

  // 掛載時取清單，預設選最新一篇；失敗（後端未上線 404/502）→ EmptyState + 重試
  const loadList = React.useCallback(() => {
    let alive = true;
    setListPhase('loading');
    setListErr('');
    api('GET', '/morning-focus/list')
      .then((rows) => {
        if (!alive) return;
        const top = Array.isArray(rows) ? rows.slice(0, 5) : [];
        setItems(top);
        setSelectedId(top.length ? top[0].id : null);
        setListPhase('ready');
      })
      .catch((e) => {
        if (!alive) return;
        setListErr((e && e.detail) || '網路連線失敗');
        setListPhase('error');
      });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => loadList(), [loadList]);

  // 切換日期 chip → 載入該篇文章
  const loadArticle = React.useCallback((id) => {
    if (!id) return undefined;
    let alive = true;
    setArtPhase('loading');
    setArtErr('');
    setArticle(null);
    api('GET', '/morning-focus/article/' + id)
      .then((data) => {
        if (!alive) return;
        setArticle(data || null);
        setArtPhase('ready');
      })
      .catch((e) => {
        if (!alive) return;
        setArtErr((e && e.detail) || '網路連線失敗');
        setArtPhase('error');
      });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => loadArticle(selectedId), [selectedId, loadArticle]);

  const sourceUrl = article && article.source_url;

  return (
    <>
      <PageHeader
        eyebrow="分析工具 · MORNING"
        title="盤前重點"
        italic="華南投顧盤前多空新聞 — 利多／利空分區重排版"
        action={sourceUrl ? (
          <Button
            variant="ghost" size="sm" icon="ext"
            onClick={() => window.open(sourceUrl, '_blank', 'noopener')}
          >在原站開啟</Button>
        ) : null}
      />

      {listPhase === 'loading' && (
        // 沿用站內 loading 樣式（Spinner + 提示、固定高度不跳版）
        <div style={uiStyle({
          height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: HR.surfaceAlt, borderRadius: 6, color: HR.inkFaint, fontSize: FontSizes.xs,
        }, { background: GxT.color.card, borderRadius: GxT.radius.card, color: GxT.color.onV, fontSize: 14 })}>
          <Spinner size={16} /> 載入盤前多空新聞中…
        </div>
      )}

      {listPhase === 'error' && (
        <Card padding="0">
          <EmptyState
            icon="x" title="盤前多空新聞載入失敗"
            hint={listErr ? `無法取得文章清單（${listErr}），請重試或稍後再來。` : '無法取得文章清單，請重試或稍後再來。'}
            action={<Button variant="primary" size="sm" icon="refresh" onClick={loadList}>重試</Button>}
          />
        </Card>
      )}

      {listPhase === 'ready' && items.length === 0 && (
        <Card padding="0">
          <EmptyState icon="folder" title="目前沒有盤前多空新聞" hint="後端尚未收錄任何文章，稍後再來看看。" />
        </Card>
      )}

      {listPhase === 'ready' && items.length > 0 && (
        <>
          {/* 日期切換 chips — 近 5 篇，樣式比照 hr-leave 篩選 chip（pill、active 實心） */}
          <div style={uiStyle({
            display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
            padding: '10px 14px', marginBottom: 14,
            background: HR.surface, border: `1px solid ${HR.ruleSoft}`, borderRadius: 6,
          }, { padding: 0, marginBottom: 16, background: 'transparent', border: 'none', borderRadius: 0 })}>
            {items.map((it) => {
              const active = it.id === selectedId;
              return (
                <button
                  key={it.id}
                  onClick={() => setSelectedId(it.id)}
                  title={it.title}
                  aria-pressed={uiPick(undefined, active)}
                  style={uiStyle({
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 999,
                    fontSize: FontSizes.xxs, fontWeight: 500, fontFamily: HR.mono,
                    border: `1px solid ${active ? HR.accent : HR.ruleSoft}`,
                    background: active ? HR.accent : HR.accentSoft,
                    color: active ? '#fff' : HR.accent,
                    cursor: 'pointer',
                    transition: 'background 120ms, border 120ms, color 120ms',
                  }, {
                    // M3 filter chip：選中 = secondary container，未選 = outlined
                    minHeight: 36, padding: '0 14px', borderRadius: GxT.radius.chip,
                    fontSize: 13, fontWeight: active ? 800 : 600, fontFamily: GxT.font, fontVariantNumeric: 'tabular-nums',
                    border: `1px solid ${active ? 'transparent' : GxT.color.ol}`,
                    background: active ? GxT.color.sc : GxT.color.card,
                    color: active ? GxT.color.onSc : GxT.color.onV,
                  })}
                >
                  {MF_dateLabel(it.date)}
                </button>
              );
            })}
          </div>

          {artPhase === 'loading' && (
            <div style={uiStyle({
              height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: HR.surfaceAlt, borderRadius: 6, color: HR.inkFaint, fontSize: FontSizes.xs,
            }, { background: GxT.color.card, borderRadius: GxT.radius.card, color: GxT.color.onV, fontSize: 14 })}>
              <Spinner size={16} /> 載入文章內容中…
            </div>
          )}

          {artPhase === 'error' && (
            <Card padding="0">
              <EmptyState
                icon="x" title="文章載入失敗"
                hint={artErr ? `無法取得這一天的內容（${artErr}），請重試。` : '無法取得這一天的內容，請重試。'}
                action={<Button variant="primary" size="sm" icon="refresh" onClick={() => loadArticle(selectedId)}>重試</Button>}
              />
            </Card>
          )}

          {artPhase === 'ready' && article && (
            <>
              <div style={uiStyle({
                fontFamily: HR.serif, fontSize: FontSizes.lg, color: HR.ink,
                letterSpacing: '-0.015em', marginBottom: 16,
              }, { fontSize: 22, fontWeight: GxT.weight.title, lineHeight: 1.3, letterSpacing: '-0.01em' })}>
                {article.title}
                <span style={uiStyle({ fontSize: FontSizes.xs, color: HR.inkFaint, fontFamily: HR.mono, marginLeft: 10 },
                  { fontWeight: 600, color: GxT.color.onV })}>
                  {article.date}
                </span>
              </div>

              {/* 每個 section（利多／利空）一張卡；sections 可能只有一區 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {(article.sections || []).map((sec, si) => (
                  <MF_SectionCard key={si} section={sec} />
                ))}
                {(article.sections || []).length === 0 && (
                  <Card padding="0">
                    <EmptyState icon="folder" title="這一天沒有新聞分區" hint="原文可能無內容，可用右上角「在原站開啟」查看。" />
                  </Card>
                )}
              </div>

              {article.disclaimer && (
                <p style={{
                  marginTop: 18, fontSize: FontSizes.trading_lg, color: HR.inkFaint, lineHeight: 1.7,
                }}>
                  {article.disclaimer}
                </p>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

window.MorningFocusPage = MorningFocusPage;
